import { useState, useCallback, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/Icon'
import { BaseFile } from '@/components/common/FileCard'
import { Plus, Search } from 'lucide-react'
import { Switch } from '@you/ui'
import FilePreview from '@/components/common/FilePreview'
import {
  useSublevelPreview,
  useParseDatasetFile,
} from '@/api/hooks/useDatasetFiles'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

interface DatasetDetailTextProps {
  fileList: BaseFile[]
  onAddFile?: () => void
  onDeleteFile?: (fileId: string) => void
  onFileSelect?: (fileId: string) => void
  selectedFileUrl?: string
}

const DatasetDetailText: React.FC<DatasetDetailTextProps> = ({
  fileList,
  onAddFile,
  onDeleteFile,
  onFileSelect,
  selectedFileUrl,
}) => {
  const { currentDataset, currentKnowledge } = useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()
  const [segments, setSegments] = useState<string[]>([])
  const [isLoadingSegments, setIsLoadingSegments] = useState(false)
  const [parseProgress, setParseProgress] = useState(0)
  const [isParsing, setIsParsing] = useState(false)
  const [parseError, setParseError] = useState(false)

  // 搜索关键词
  const [keyword, setKeyword] = useState('')
  // 当前选中的文档
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('')
  // 是否显示原始文件预览
  const [showOriginalPreview, setShowOriginalPreview] = useState(true)

  // 初始化选中第一个文件
  useEffect(() => {
    if (fileList.length > 0 && !selectedDocumentId) {
      const firstFileId = fileList[0].id
      setSelectedDocumentId(firstFileId)
      onFileSelect?.(firstFileId)
    }
  }, [fileList, selectedDocumentId, onFileSelect])

  // 过滤文件列表
  const filteredFileList = fileList.filter((file) =>
    file.name.toLowerCase().includes(keyword.toLowerCase())
  )

  // 获取当前选中的文档
  const selectedDocument = fileList.find((doc) => doc.id === selectedDocumentId)

  // 使用解析和预览 hooks
  const { trigger: getFilePreview } = useSublevelPreview()
  const { trigger: parseFile } = useParseDatasetFile()

  /**
   * 处理文档选择
   */
  const handleDocumentSelect = useCallback(
    (documentId: string) => {
      setSelectedDocumentId(documentId)
      onFileSelect?.(documentId)
    },
    [onFileSelect]
  )

  /**
   * 处理删除文件
   */
  const handleDeleteFile = useCallback(
    (fileId: string, event: React.MouseEvent) => {
      event.stopPropagation() // 阻止事件冒泡
      onDeleteFile?.(fileId)
    },
    [onDeleteFile]
  )

  /**
   * 处理切换预览模式
   */
  const handleTogglePreview = useCallback((checked: boolean) => {
    setShowOriginalPreview(checked)
  }, [])

  // 加载分段预览数据
  const loadSegmentPreview = useCallback(
    async (fileId: string) => {
      if (
        !fileId ||
        !currentDataset?.id ||
        !currentKnowledge?.id ||
        !currentWorkspace?.id
      )
        return

      setIsLoadingSegments(true)
      try {
        const response = await getFilePreview({
          datasetFileId: fileId,
          datasetId: currentDataset.id,
          knowledgeBaseId: currentKnowledge.id,
          workspaceId: currentWorkspace.id,
        })
        setSegments(response.segments)
      } catch (error) {
        console.error('加载分段预览失败:', error)
        setSegments([])
      } finally {
        setIsLoadingSegments(false)
      }
    },
    [
      currentDataset?.id,
      currentKnowledge?.id,
      currentWorkspace?.id,
      getFilePreview,
    ]
  )

  // 当选择文件时加载分段预览
  useEffect(() => {
    if (selectedDocumentId) {
      loadSegmentPreview(selectedDocumentId)
    }
  }, [selectedDocumentId, loadSegmentPreview])

  /**
   * 处理文档解析
   */
  const handleParse = useCallback(async () => {
    if (
      !selectedDocumentId ||
      !currentDataset?.id ||
      !currentKnowledge?.id ||
      !currentWorkspace?.id
    )
      return

    setIsParsing(true)
    setParseError(false)
    setParseProgress(0)

    // 启动进度条动画
    const progressInterval = setInterval(() => {
      setParseProgress((prev) => {
        if (prev >= 90) return prev // 停在90%等待实际完成
        return prev + Math.random() * 15
      })
    }, 500)

    try {
      await parseFile({
        datasetId: currentDataset.id,
        knowledgeBaseId: currentKnowledge.id,
        workspaceId: currentWorkspace.id,
        datasetFileIds: [selectedDocumentId],
      })

      // 解析成功，进度条达到100%
      setParseProgress(100)
      setTimeout(() => {
        setIsParsing(false)
        setParseProgress(0)
        // 重新加载分段预览
        loadSegmentPreview(selectedDocumentId)
      }, 500)
    } catch (error) {
      console.error('解析失败:', error)
      setParseError(true)
      setParseProgress(0)
      setIsParsing(false)
    } finally {
      clearInterval(progressInterval)
    }
  }, [
    selectedDocumentId,
    currentDataset?.id,
    currentKnowledge?.id,
    currentWorkspace?.id,
    parseFile,
    loadSegmentPreview,
  ])

  /**
   * 渲染文档列表项
   */
  const renderDocumentItem = useCallback(
    (document: BaseFile) => {
      const isSelected = document.id === selectedDocumentId

      return (
        <div
          key={document.id}
          className={`
            h-12 px-2 rounded-lg flex items-center justify-between cursor-pointer transition-colors
            ${
              isSelected
                ? 'bg-[#F9FAFF] text-[#333333]'
                : 'text-[#333333] hover:bg-gray-50'
            }
          `}
          onClick={() => handleDocumentSelect(document.id)}
        >
          <span className="text-base truncate flex-1">{document.name}</span>
          <button
            title="删除"
            onClick={(e) => handleDeleteFile(document.id, e)}
            className="flex items-center justify-center w-6 h-6 text-gray-500 hover:text-red-500 transition-colors"
          >
            <Icon name="delete" size={20} />
          </button>
        </div>
      )
    },
    [selectedDocumentId, handleDocumentSelect, handleDeleteFile]
  )

  /**
   * 渲染搜索和添加区域
   */
  const renderSearchAndAdd = () => (
    <div className="flex items-center justify-between p-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="搜索"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="px-0 pl-9 pr-2"
        />
      </div>
      <Button
        size="sm"
        onClick={onAddFile}
        className="w-[100px] h-8 flex-none gradient-primary ml-2"
      >
        <Plus size={14} />
        添加
      </Button>
    </div>
  )

  /**
   * 渲染文件列表
   */
  const renderFileList = () => (
    <div className="flex-1 px-4 overflow-y-auto">
      <div className="space-y-2 group">
        {filteredFileList.length > 0 ? (
          filteredFileList.map(renderDocumentItem)
        ) : (
          <div className="text-center text-gray-500 py-8">
            <div className="text-sm">
              {keyword ? '未找到匹配的文档' : '暂无文档'}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  /**
   * 渲染左侧文档列表
   */
  const renderDocumentList = () => (
    <div className="flex flex-col h-full bg-white rounded-lg">
      {renderSearchAndAdd()}
      {renderFileList()}
    </div>
  )

  /**
   * 渲染右侧预览头部
   */
  const renderPreviewHeader = () => (
    <div className="flex items-center justify-between mb-2">
      {/* 左侧：当前文件名称 */}
      <div className="flex-1">
        <h3 className="text-lg font-bold text-[#655093] truncate">
          {selectedDocument?.name || '请选择文档'}
        </h3>
      </div>

      {/* 右侧：预览切换开关 */}
      <div className="flex items-center space-x-2">
        <Switch
          checked={showOriginalPreview}
          onCheckedChange={handleTogglePreview}
        />
        <label className="text-lg ml-2 text-[#737688]">预览原始文件</label>
      </div>
    </div>
  )

  /**
   * 渲染原始文件预览内容
   */
  const renderOriginalContent = () => (
    <FilePreview fileUrl={selectedFileUrl || ''} />
  )

  /**
   * 渲染分段预览内容
   */
  const renderSegmentContent = () => (
    <div className="h-full overflow-hidden">
      {isLoadingSegments ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <div className="text-lg mb-2">加载中...</div>
          </div>
        </div>
      ) : segments.length > 0 ? (
        <div className="h-full overflow-y-auto">
          <div className="space-y-4">
            {segments.map((segment, index) => (
              <div
                key={index}
                className="p-4 rounded-lg border border-[#F0F3FF] bg-[#F9FAFF]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#4E5969]">
                    段落 {index + 1}
                  </span>
                </div>
                <div className="text-sm text-[#1D2129] whitespace-pre-wrap">
                  {segment}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <div className="text-lg mb-2">📋</div>
            <div>当前文档未做分段解析</div>
            {parseError && (
              <div className="text-red-500 text-sm mt-1">解析失败，请重试</div>
            )}
            {isParsing ? (
              <div className="mt-4 w-64">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{ width: `${parseProgress}%` }}
                  />
                </div>
              </div>
            ) : (
              <Button
                onClick={handleParse}
                className="mt-4 bg-gradient-primary"
                disabled={!selectedDocument}
              >
                点击解析
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  /**
   * 渲染右侧预览区域
   */
  const renderPreviewArea = () => (
    <div className="flex flex-col h-full">
      {/* 头部：文件名称和切换开关 */}
      {renderPreviewHeader()}

      {/* 内容区域 */}
      <div className="flex-1 flex min-h-0">
        <div
          className={`
          overflow-hidden
          transition-all duration-300 ease-in-out
          ${showOriginalPreview ? 'flex-1 bg-white rounded-lg p-2 mr-2' : 'w-0'}
        `}
        >
          <div className="h-full overflow-y-auto">
            {renderOriginalContent()}
          </div>
        </div>
        <div className="flex-1 bg-white rounded-lg p-4">
          {renderSegmentContent()}
        </div>
      </div>
    </div>
  )

  return (
    <div className="h-full min-w-[1200px] p-2">
      {/* 两栏布局 */}
      <div className="flex gap-2 h-full">
        {/* 左侧：文档列表 */}
        <div className="w-[350px] flex-none">{renderDocumentList()}</div>

        {/* 右侧：预览区域 */}
        <div className="flex-1">{renderPreviewArea()}</div>
      </div>
    </div>
  )
}

export default DatasetDetailText
